import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import Fade from "@mui/material/Fade";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import { Buffer } from "buffer";
import clsx from "clsx";
import * as React from "react";
import { ReactComponent as CheckIcon } from "../../common/assets/images/iconCheck.svg";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl, AppUrl } from "../../config";
import BranchOfficeDeposits from "../../containers/BranchOfficeDeposits";
import { initialAppState } from "../../common/reducers/appState";
import {
  standardComparisonOptions,
  subOptionLookup,
} from "./PeerGroupUtilities";
import { KeyValue, Query } from "../../interfaces";
import ModalDialog from "../ModalDialog";
import AddButton from "./AddButton";
import MoreOptionsMenu from "./MoreOptionsMenu";
import PeerGroupCriteriaTable from "./PeerGroupCriteriaTable";
import { isEqual } from "lodash";
import { styled, keyframes } from "@mui/material/styles";

const PREFIX = "CustomComponent";

const classes = {
  groupBox: `${PREFIX}-groupBox`,
  groupBox2: `${PREFIX}-groupBox2`,
  groupBox3: `${PREFIX}-groupBox3`,
  altColor: `${PREFIX}-altColor`,
  columnHeader: `${PREFIX}-columnHeader`,
  groupHeader: `${PREFIX}-groupHeader`,
  rightIcon: `${PREFIX}-rightIcon`,
  leftIcon: `${PREFIX}-leftIcon`,
  duplicateButton: `${PREFIX}-duplicateButton`,
  altDuplicateButton: `${PREFIX}-altDuplicateButton`,
  customDuplicateButton: `${PREFIX}-customDuplicateButton`,
  groupName: `${PREFIX}-groupName`,
  bottom: `${PREFIX}-bottom`,
  bottom2: `${PREFIX}-bottom2`,
  popperButtons: `${PREFIX}-popperButtons`,
  nextStep: `${PREFIX}-nextStep`,
};

const slideInLeft = keyframes`
  from {
    margin-left: 100%;
    margin-right: -100%;
  }
  to {
    margin-left: 0;
    margin-right: 0;
  }
`;

const slideInRight = keyframes`
  from {
    margin-left: -100%;
    margin-right: 100%;
  }
  to {
    margin-left: 0;
    margin-right: 0;
  }
`;

const duplicated = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const deleted = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const Root = styled("div")(({ theme }) => ({
  [`.${classes.groupBox}, .${classes.groupBox3}`]: {
    "padding": ".5rem",
    "borderColor": "#70643e",
    "backgroundColor": "#ede7d3",
    "&.duplicatedGroup": {
      animation: `${duplicated} 300ms ease-in-out`,
    },
    "&.deleteGroup": {
      animation: `${deleted} 300ms ease-in-out`,
    },
    "&.movedGroupLeft": {
      "position": "relative",
      "animation": `${slideInLeft} 300ms ease-in-out`,
      "& button": {
        pointerEvents: "none",
      },
    },
    "&.movedGroupRight": {
      "position": "relative",
      "animation": `${slideInRight} 300ms ease-in-out`,
      "& button": {
        pointerEvents: "none",
      },
    },
  },

  [`.${classes.groupBox2}`]: {
    "padding": ".5rem",
    "borderColor": "#00908C",
    "backgroundColor": "#E6FFFF",
    "&.duplicatedGroup": {
      animation: `${duplicated} 300ms ease-in-out`,
    },
    "&.deleteGroup": {
      animation: `${deleted} 300ms ease-in-out`,
    },
    "&.movedGroupLeft": {
      "position": "relative",
      "animation": `${slideInLeft} 300ms ease-in-out`,
      "& button": {
        pointerEvents: "none",
      },
    },
    "&.movedGroupRight": {
      "position": "relative",
      "animation": `${slideInRight} 300ms ease-in-out`,
      "& button": {
        pointerEvents: "none",
      },
    },
  },

  [`.${classes.altColor}`]: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.main,
  },
  [`.${classes.columnHeader}, .${classes.groupHeader}`]: {
    color: theme.palette.primary.main,
    fontSize: ".75rem",
    fontWeight: 700,
  },
  [`.${classes.columnHeader}`]: {
    paddingLeft: ".3rem",
  },
  [`.${classes.groupHeader}`]: {
    lineHeight: ".75rem",
  },
  [`.${classes.rightIcon}, .${classes.leftIcon}`]: {
    "padding": ".25rem",
    "margin": 0,
    "float": "right",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  [`.${classes.leftIcon}`]: {
    float: "left",
  },
  [`.${classes.duplicateButton}`]: {
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": "bold",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "#b49859",
    },
  },
  [`.${classes.altDuplicateButton}`]: {
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": "bold",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "#7c97bc",
    },
  },
  [`.${classes.customDuplicateButton}`]: {
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": "bold",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "#e6ffff",
      color: theme.palette.primary.main,
    },
  },
  [`.${classes.groupName}`]: {
    paddingBottom: 0,
    fontWeight: 700,
    color: theme.palette.primary.main,
    wordBreak: "break-word",
  },
  [`.${classes.bottom}, .${classes.bottom2}`]: {
    "padding": ".25rem",
    "margin": "1rem -.5rem -.5rem -.5rem",
    "& span, & svg, & p": {
      color: "#ffffff",
      fontWeight: 700,
    },
  },
  [`.${classes.bottom}`]: {
    backgroundColor: "#70643e",
  },
  [`.${classes.bottom2}`]: {
    backgroundColor: theme.palette.primary.main,
  },
  [`.${classes.popperButtons}`]: {
    "display": "block",
    "marginTop": 0,
    "width": "100%",
    "textAlign": "left",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  [`.${classes.nextStep}`]: {
    "display": "flex",
    "width": "fit-content",
    "height": "fit-content",
    "cursor": "pointer",
    "marginTop": "1rem",
    "padding": 0,
    "& svg": {
      marginRight: "1rem",
      height: 80,
      width: 80,
    },
    "& div": {
      "padding": "1rem 0",
      "& p": {
        textAlign: "left",
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "1.25rem",
      },
    },
    "&:hover, &.Mui-focusVisible": {
      "backgroundColor": "transparent",
      "& #checkStatic_Rectangle": {
        fill: theme.palette.primary.main,
        stroke: "#a58922",
      },
      "& #checkStatic_Combined-Shape": {
        fill: "#ffffff",
      },
    },
  },
}));
interface Props {
  appState: Query;
  groups: string[];
  updatePeerGroups: (peerGroups: string[]) => void;
  updatePeerGroupNames: (groupNames: string[]) => void;
  updatePGCStep: (pgcStep: string) => void;
  updateUi: (uiField: string, uiValue: KeyValue[] | string) => void;
  customCompare?: boolean;
  data?: KeyValue[];
  userId?: any;
}

export default function PeerGroups({
  appState,
  groups,
  updatePeerGroups,
  updatePeerGroupNames,
  updatePGCStep,
  updateUi,
  customCompare,
  data,
  userId,
}: Props) {
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const [reportPeriod, setReportPeriod] = React.useState("");
  const initAppState = {
    ...initialAppState.SOD,
    filters: {
      ...initialAppState.SOD.filters,
      lastYear:
        reportPeriod?.slice(0, 4) ??
        appState.filters?.reportPeriod[0].slice(0, 4),
      stalpbr: appState.filters?.stalp,
      zipbr: appState.filters?.zip,
    },
  };

  const branchOfficeModal = (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...initAppState,
          filters: { ...initAppState.filters, cert: open },
        }}
      />
    </ModalDialog>
  );
  const standardPeerGroupOptions: string[] = standardComparisonOptions.map(
    (option) => option.value,
  );
  const standardPeerGroupCustomCompare =
    appState.ui?.savedGroupId &&
    standardPeerGroupOptions.includes(appState.ui.savedGroupId);
  const groupId =
    customCompare && !standardPeerGroupCustomCompare ? "groupID" : "short_name";
  const moveButtonProps = (direction: string, index: number) => {
    return {
      onClick: () => moveGroup(selectedPeerGroups, index, direction),
      onMouseEnter: () => setCurrentMoveButton(`move-${direction}-${index}`),
      onMouseLeave: () => setCurrentMoveButton(""),
      onFocus: () => setCurrentMoveButton(`move-${direction}-${index}`),
      onBlur: () => setCurrentMoveButton(""),
    };
  };

  const [selectedPeerGroups, setSelectedPeerGroups] = React.useState<
    KeyValue[]
  >([]);

  const [currentComparisonType, setCurrentComparisonType] = React.useState(
    appState.ui.comparisonType,
  );
  const [currentSavedGroupId, setCurrentSavedGroupId] = React.useState(
    appState.ui.savedGroupId,
  );
  const [currentSubOptionsSelected, setCurrentSubOptionsSelected] =
    React.useState(appState.ui.subOptionsSelected);
  const [updateMove, setUpdateMove] = React.useState({
    update: false,
    array: [] as KeyValue[],
  });

  const [currentMoveButton, setCurrentMoveButton] = React.useState("");
  const [closeMenu, setCloseMenu] = React.useState(false);

  const [openModal, setOpenModal] = React.useState(false);
  const [currentGroup, setCurrentGroup] = React.useState({
    filters: [{}] as KeyValue[],
    title: "",
    custom: false,
    appState: undefined,
  });
  const [currentGroupMove, setCurrentGroupMove] = React.useState("");
  const [currentGroupMoveOther, setCurrentGroupMoveOther] = React.useState("");

  const [currentGroupDuplicated, setCurrentGroupDuplicated] =
    React.useState("");
  const [displayMoveClass, setDisplayMoveClass] = React.useState(false);

  function openGroupTable(index: number) {
    setOpenModal(true);
    setCurrentGroup({
      filters: selectedPeerGroups[index].filter_criteria,
      title: selectedPeerGroups[index].name,
      custom: selectedPeerGroups[index].is_custom,
      appState: selectedPeerGroups[index].saved_state
        ? JSON.parse(
            Buffer.from(
              selectedPeerGroups[index].saved_state,
              "base64",
            ).toString("utf-8"),
          )
        : undefined,
    });
  }

  function moveGroup(
    groupsArray: KeyValue[],
    currentIndex: number,
    direction: string,
  ) {
    const currentGroupMove = groupsArray[currentIndex];
    const toIndex = direction === "right" ? currentIndex + 1 : currentIndex - 1;
    const currentGroupMoveOther = groupsArray[toIndex];

    setDisplayMoveClass(true);
    setCurrentGroupMove(`${currentGroupMove[groupId]}_${toIndex}_${direction}`);
    setCurrentGroupMoveOther(
      `${currentGroupMoveOther[groupId]}_${currentIndex}_${direction}`,
    );

    groupsArray.splice(currentIndex, 1);
    groupsArray.splice(toIndex, 0, currentGroupMove);
    setUpdateMove({ update: true, array: groupsArray });
  }

  function duplicateGroup(groupsArray: KeyValue[], currentIndex: number) {
    const currentGroupDuplicate = groupsArray[currentIndex];
    setDisplayMoveClass(true);
    setCurrentGroupDuplicated(
      `${currentGroupDuplicate[groupId]}_${currentIndex + 1}`,
    );
    groupsArray.splice(currentIndex, 0, currentGroupDuplicate);
    setUpdateMove({ update: true, array: groupsArray });
  }

  function deleteFromGroup(groupsArray: KeyValue[], index: number) {
    setCloseMenu(true);
    groupsArray.splice(index, 1);

    setUpdateMove({ update: true, array: groupsArray });
  }

  React.useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(selectedPeerGroups)) {
      setSelectedPeerGroups(data);
    }
  }, [data, selectedPeerGroups]);

  const [tableAppState, setTableAppState] = React.useState(appState.ui);
  React.useEffect(() => {
    if (!isEqual(appState.ui, tableAppState)) {
      setTableAppState(appState.ui);
    }
  }, [appState.ui, tableAppState]);
  React.useEffect(() => {
    if (updateMove.update) {
      setSelectedPeerGroups(updateMove.array);
      updatePeerGroups(
        updateMove.array.map((value: KeyValue) => value[groupId]),
      );
      setUpdateMove({ update: false, array: [] });
      setCloseMenu(false);
    }
  }, [groupId, updateMove.array, updateMove.update, updatePeerGroups]);
  const [initialLoad, setInitialLoad] = React.useState(true);
  //gets the standard peer group
  React.useEffect(() => {
    if (
      customCompare &&
      standardPeerGroupCustomCompare &&
      ((appState.ui?.savedGroupId &&
        appState.ui.savedGroupId !== currentSavedGroupId) ||
        initialLoad ||
        JSON.stringify(appState.ui.subOptionsSelected) !==
          currentSubOptionsSelected)
    ) {
      initialLoad && setInitialLoad(false);
      setCurrentSavedGroupId(appState.ui.savedGroupId);
      let selectedKeys: string[] = [];
      let currentPeerGroups: string[] = [];
      if (
        appState.ui?.subOptionsSelected &&
        Object.keys(appState.ui.subOptionsSelected).length > 0 &&
        !isEqual(currentSubOptionsSelected, appState.ui.subOptionsSelected)
      ) {
        setCurrentSubOptionsSelected(appState.ui.subOptionsSelected);
        selectedKeys = Object.keys(appState.ui.subOptionsSelected).filter(
          (key) => appState.ui.subOptionsSelected[key],
        );
      }
      const getGroupsByValue = (value: string): string[] | undefined => {
        return standardComparisonOptions.find(
          (option) => option.value === value,
        )?.groups;
      };
      currentPeerGroups = getGroupsByValue(appState.ui.savedGroupId) ?? [
        appState.ui.savedGroupId,
      ];
      if (selectedKeys.length) {
        currentPeerGroups = currentPeerGroups.filter((group: string) =>
          selectedKeys.includes(subOptionLookup[group]),
        );
      } else if (
        currentPeerGroups.length > 1 &&
        currentPeerGroups.includes("allInst")
      ) {
        //this is for the case where it loads the all institutions peer group card with all the other options
        currentPeerGroups = currentPeerGroups.filter(
          (group: string) => group !== "allInst",
        );
      }
      if (currentPeerGroups?.length) {
        (async () => {
          await fetchData(
            `${ApiBaseUrl()}/peer_groups?filters=short_name:(${currentPeerGroups.join(
              " OR ",
            )})`,
          )
            .then((response) => {
              setSelectedPeerGroups(response.data);
            })
            .catch((error) => {
              console.error(`${error.name} : ${error.message}`);
            });
        })();
      }
    }
  }, [
    appState.ui.savedGroupId,
    appState.ui.standardPeerGroups,
    appState.ui.subOptionsSelected,
    currentSavedGroupId,
    currentSubOptionsSelected,
    customCompare,
    initialLoad,
    standardPeerGroupCustomCompare,
  ]);

  React.useEffect(() => {
    if (
      !customCompare &&
      (appState.ui.comparisonType !== currentComparisonType || initialLoad)
    ) {
      if (initialLoad) {
        setInitialLoad(false);
      }
      const peerGroupIds =
        initialLoad && appState.ui.peerGroups.length > 0
          ? appState.ui.peerGroups
          : groups;

      setCurrentComparisonType(appState.ui.comparisonType);
      if (peerGroupIds && peerGroupIds.length > 0) {
        (async () => {
          await fetchData(
            `${ApiBaseUrl()}/peer_groups?filters=short_name:(${peerGroupIds.join(
              " OR ",
            )})`,
          )
            .then((response) => {
              const peerGroupData = peerGroupIds.map((groupID: string) => {
                return response.data.find(
                  (data: KeyValue) => data[groupId] === groupID,
                );
              });
              setSelectedPeerGroups(peerGroupData);
              updatePeerGroups(
                peerGroupData.map((value: KeyValue) => value[groupId]),
              );
            })
            .catch((error) => {
              console.error(`${error.name} : ${error.message}`);
            });
        })();
      } else {
        setSelectedPeerGroups([]);
        updatePeerGroups([]);
      }
    }
  }, [
    appState.ui.comparisonType,
    appState.ui.peerGroups,
    currentComparisonType,
    customCompare,
    groupId,
    groups,
    initialLoad,
    updatePeerGroups,
  ]);
  React.useEffect(() => {
    if (
      appState.ui.peerGroupNames &&
      appState.ui.peerGroups &&
      appState.ui.peerGroupNames.length !== appState.ui.peerGroups.length
    ) {
      updatePeerGroupNames(appState.ui.peerGroups);
    }
  }, [
    appState.ui.peerGroupNames,
    appState.ui.peerGroups,
    updatePeerGroupNames,
  ]);

  React.useEffect(() => {
    //removes classes after 500ms
    if (displayMoveClass) {
      const timer = setTimeout(() => {
        setDisplayMoveClass(false);
        setCurrentGroupDuplicated("");
        setCurrentGroupMove("");
        setCurrentGroupMoveOther("");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [displayMoveClass]);

  const addPeerGroupToReport = (name: string) => {
    let customPeerGroups: any[] = appState.ui.customPeerGroups ?? [];
    if (standardPeerGroupCustomCompare) {
      const standardGroups = appState.ui.standardPeerGroups;
      updateUi("standardPeerGroups", [...standardGroups, name]);
    } else if (!customPeerGroups.includes(name)) {
      customPeerGroups = [...customPeerGroups, name];
      updateUi("customPeerGroups", customPeerGroups);
    }
  };

  const showTools =
    (appState.ui?.customPeerGroups?.length +
      appState.ui?.individualCert?.length +
      appState.ui?.standardGroupSearchPills?.length >
      0 &&
      appState.ui.pgcStep === "step2") ||
    appState.ui.comparisonType;
  return selectedPeerGroups.length > 0 && selectedPeerGroups[0] && groups ? (
    <Root>
      <Grid container spacing={2}>
        {selectedPeerGroups.map((selectGroup: KeyValue, index: number) => {
          const currentGroupName = selectGroup?.name;
          const currentGroupId = selectGroup?.[groupId];
          let customPeerGroup = false;
          let standardPeerGroup = false;
          let indivCert = false;
          const standardGroups = standardComparisonOptions
            .map((comp) => comp.groups)
            .flat();
          if (
            data?.[index]?.peerGroupTitle &&
            data?.[index]?.peerGroupTitle === "Peer Group"
          ) {
            if (data?.[index]?.is_custom) {
              customPeerGroup = true;
            } else {
              standardPeerGroup = true;
            }
          } else if (data?.[index]?.peerGroupTitle?.startsWith("FDIC Cert")) {
            indivCert = true;
          }
          return (
            <Grid
              key={index}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              {!appState.ui.savedPGCSearch && (
                <Typography variant="body1" className={classes.columnHeader}>
                  Column {index + 1}
                </Typography>
              )}
              {open !== "" && open === currentGroupId && branchOfficeModal}
              <Paper
                variant="outlined"
                className={clsx(
                  selectGroup?.altColor && classes.altColor,
                  customPeerGroup
                    ? classes.groupBox2
                    : standardPeerGroup
                      ? classes.groupBox3
                      : classes.groupBox,
                  displayMoveClass &&
                    currentGroupMove === `${currentGroupId}_${index}_right` &&
                    "movedGroupRight",
                  displayMoveClass &&
                    currentGroupMoveOther ===
                      `${currentGroupId}_${index}_right` &&
                    "movedGroupLeft",
                  displayMoveClass &&
                    currentGroupMove === `${currentGroupId}_${index}_left` &&
                    "movedGroupLeft",
                  displayMoveClass &&
                    currentGroupMoveOther ===
                      `${currentGroupId}_${index}_left` &&
                    "movedGroupRight",
                  displayMoveClass &&
                    currentGroupDuplicated === `${currentGroupId}_${index}` &&
                    "duplicatedGroup",
                )}
                id={`peer-group-${index}`}
              >
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="body1" className={classes.groupHeader}>
                      {customPeerGroup
                        ? `Custom ${data?.[index]?.peerGroupTitle}`
                        : standardPeerGroup
                          ? `Standard ${data?.[index]?.peerGroupTitle}`
                          : data?.[index]?.peerGroupTitle
                            ? data?.[index]?.peerGroupTitle
                            : "Standard Peer Group"}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <MoreOptionsMenu
                      id={currentGroupId}
                      popperPosition="right-start"
                      toolTipText="More Options"
                      icon={<MoreHorizIcon />}
                      close={closeMenu}
                    >
                      <Button
                        id="view-criteria"
                        className={classes.popperButtons}
                        onClick={() => {
                          if (
                            customPeerGroup ||
                            standardGroups.includes(currentGroupId)
                          ) {
                            openGroupTable(index);
                          } else {
                            setReportPeriod(
                              appState.ui?.individualCert[
                                index
                              ]?.reportPeriod?.slice(0, 4),
                            );
                            setOpen(currentGroupId);
                            setName(currentGroupName);
                          }
                        }}
                      >
                        {!indivCert
                          ? "View Peers & Criteria"
                          : "View Institution Details"}
                      </Button>
                      {!showTools ? (
                        <Button
                          id="add-group"
                          className={classes.popperButtons}
                          onClick={() =>
                            addPeerGroupToReport(
                              standardPeerGroupCustomCompare
                                ? currentGroupId
                                : currentGroupName,
                            )
                          }
                        >
                          Add Selection to Report
                        </Button>
                      ) : (
                        <Button
                          id="delete-group"
                          className={classes.popperButtons}
                          disabled={appState.ui.peerGroups.length < 2}
                          onClick={() => {
                            if (
                              selectGroup?.peerGroupTitle?.startsWith(
                                "FDIC Cert",
                              )
                            ) {
                              const updatedCerts =
                                appState.ui.individualCert.filter(
                                  (obj: KeyValue) =>
                                    obj.value !== currentGroupId,
                                );
                              updateUi("individualCert", updatedCerts);
                            } else if (selectGroup?.is_custom) {
                              const updatedCustomPills =
                                appState.ui.customPeerGroups.filter(
                                  (obj: KeyValue) =>
                                    obj.value !== currentGroupId,
                                );
                              updateUi("customPeerGroups", updatedCustomPills);
                            } else {
                              const updatedPills =
                                appState.ui.standardGroupSearchPills.filter(
                                  (obj: KeyValue) =>
                                    obj.short_name !== currentGroupId,
                                );
                              const updatedGroups =
                                appState.ui.standardPeerGroups.filter(
                                  (group: string) => group !== currentGroupId,
                                );
                              updateUi("standardPeerGroups", updatedGroups);
                              updateUi(
                                "standardGroupSearchPills",
                                updatedPills,
                              );
                            }
                            deleteFromGroup(
                              selectedPeerGroups,
                              appState.ui.peerGroups.indexOf(currentGroupId),
                            );
                          }}
                        >
                          Delete From Comparisons
                        </Button>
                      )}
                    </MoreOptionsMenu>
                  </Grid>
                </Grid>
                <Typography variant="body2" className={classes.groupName}>
                  {currentGroupName}
                </Typography>
                <Box
                  className={
                    selectGroup?.altColor ? classes.bottom2 : classes.bottom
                  }
                  style={customPeerGroup ? { backgroundColor: "#00908C" } : {}}
                >
                  {showTools && (
                    <Grid container spacing={0}>
                      <Grid size={4}>
                        {index > 0 && (
                          <Fade
                            in={currentMoveButton !== `move-right-${index}`}
                          >
                            <Button
                              id={`move-left-${index}`}
                              aria-label={`move ${currentGroupName} left`}
                              className={classes.leftIcon}
                              {...moveButtonProps("left", index)}
                            >
                              <ArrowLeftIcon />
                              <Fade
                                in={currentMoveButton === `move-left-${index}`}
                              >
                                <Typography>Move</Typography>
                              </Fade>
                            </Button>
                          </Fade>
                        )}
                      </Grid>
                      <>
                        <Grid size={4}>
                          <Button
                            id={`duplicate-column-${index}`}
                            className={
                              customPeerGroup
                                ? classes.customDuplicateButton
                                : selectGroup?.altColor
                                  ? classes.altDuplicateButton
                                  : classes.duplicateButton
                            }
                            onClick={() =>
                              duplicateGroup(selectedPeerGroups, index)
                            }
                          >
                            Duplicate Column
                          </Button>
                        </Grid>
                        <Grid size={4}>
                          {selectedPeerGroups.length !== index + 1 && (
                            <Fade
                              in={currentMoveButton !== `move-left-${index}`}
                            >
                              <Button
                                id={`move-right-${index}`}
                                aria-label={`move ${currentGroupName} right`}
                                className={classes.rightIcon}
                                {...moveButtonProps("right", index)}
                              >
                                <Fade
                                  in={
                                    currentMoveButton === `move-right-${index}`
                                  }
                                >
                                  <Typography>Move</Typography>
                                </Fade>
                                <ArrowRightIcon />
                              </Button>
                            </Fade>
                          )}
                        </Grid>
                      </>
                    </Grid>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {showTools && (
        <Grid container spacing={2}>
          {!appState.ui.comparisonType && (
            <Grid size={4}>
              <AddButton
                onClick={() => {
                  updatePGCStep("step1");
                  updatePeerGroups([]);
                }}
                title={"Add a column to compare"}
                path={`${AppUrl("PeerGroup")}/customized/search`}
              />
            </Grid>
          )}
          <Grid size={4}>
            <Button
              onClick={() => updatePGCStep("step3")}
              className={classes.nextStep}
              id="pgc-next-step"
            >
              <CheckIcon />
              <div>
                <Typography>Next Step: Report Type</Typography>
              </div>
            </Button>
          </Grid>
        </Grid>
      )}
      <ModalDialog
        title={currentGroup.title}
        display={openModal}
        setDisplay={setOpenModal}
        maxWidth="lg"
      >
        <PeerGroupCriteriaTable
          currentGroup={currentGroup}
          appState={appState}
        />
      </ModalDialog>
    </Root>
  ) : (
    <></>
  );
}
